import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SessionGuard from "@/components/SessionGuard";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import LeadFormPage from "@/pages/LeadFormPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import DashboardPage from "@/pages/DashboardPage";
import NewScreeningPage from "@/pages/NewScreeningPage";
import ScreeningDetailPage from "@/pages/ScreeningDetailPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import AdminClientsPage from "@/pages/AdminClientsPage";
import AdminClientDetailPage from "@/pages/AdminClientDetailPage";
import DeletionLogPage from "@/pages/DeletionLogPage";
import AdminStaffPage from "@/pages/AdminStaffPage";
import CollectionsPage from "@/pages/CollectionsPage";
import NewClaimPage from "@/pages/NewClaimPage";
import ClaimDetailPage from "@/pages/ClaimDetailPage";
import GroupDetailPage from "@/pages/GroupDetailPage";
import OutreachPage from "@/pages/OutreachPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SecurityPage from "@/pages/SecurityPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import PublicApplicationPage from "@/pages/PublicApplicationPage";
import OperationsDashboardPage from "@/pages/OperationsDashboardPage";
import OperationsQueuePage from "@/pages/OperationsQueuePage";
import OpsCaseQueuePage from "@/pages/OpsCaseQueuePage";
import EmploymentVerificationPage from "@/pages/EmploymentVerificationPage";
import LandlordVerificationPage from "@/pages/LandlordVerificationPage";
import DocumentValidationPage from "@/pages/DocumentValidationPage";
import AccessDeniedPage from "@/pages/AccessDeniedPage";
import SubscriberAcknowledgmentPage from "@/pages/SubscriberAcknowledgmentPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import IdleTimeout from "@/components/IdleTimeout";
import ErrorBoundary from "@/components/ErrorBoundary";

function PublicHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <SessionGuard />
            <IdleTimeout />
            <Routes>
            <Route path="/" element={<PublicHome />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/request-information" element={<LeadFormPage kind="info" />} />
            <Route path="/schedule-demo" element={<LeadFormPage kind="demo" />} />
            <Route path="/request-pricing" element={<LeadFormPage kind="pricing" />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/apply/:token" element={<PublicApplicationPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
            <Route path="/screenings/new" element={<ProtectedRoute roles={["property_manager", "ntn_staff"]} requiredPermission="reports.order"><NewScreeningPage /></ProtectedRoute>} />
            <Route path="/access-denied" element={<ProtectedRoute><AccessDeniedPage /></ProtectedRoute>} />
            <Route path="/subscriber-acknowledgment" element={<ProtectedRoute roles={["property_manager"]}><SubscriberAcknowledgmentPage /></ProtectedRoute>} />
            <Route path="/screenings/:id" element={<ProtectedRoute><ScreeningDetailPage /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute roles={["ntn_staff"]}><AdminClientsPage /></ProtectedRoute>} />
            <Route path="/admin/clients/:companyId" element={<ProtectedRoute roles={["ntn_staff"]}><AdminClientDetailPage /></ProtectedRoute>} />
            <Route path="/admin/staff" element={<ProtectedRoute roles={["ntn_staff"]}><AdminStaffPage /></ProtectedRoute>} />
            <Route path="/admin/deletion-log" element={<ProtectedRoute roles={["ntn_staff"]}><DeletionLogPage /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute roles={["ntn_staff"]}><AdminLeadsPage /></ProtectedRoute>} />
            {/* NTN Staff Operations Platform — the single source of truth for
                Employment / Landlord / Document Validation work. The legacy
                "Verification Queue" (/queue*) has been retired; any old
                bookmark now falls through to the catch-all 404 → "/". */}
            <Route path="/staff-ops" element={<ProtectedRoute roles={["ntn_staff"]}><OperationsDashboardPage /></ProtectedRoute>} />
            <Route path="/staff-ops/cases" element={<ProtectedRoute roles={["ntn_staff"]}><OpsCaseQueuePage /></ProtectedRoute>} />
            <Route path="/staff-ops/:track" element={<ProtectedRoute roles={["ntn_staff"]}><OperationsQueuePage /></ProtectedRoute>} />
            <Route path="/staff-ops/employment/:sid" element={<ProtectedRoute roles={["ntn_staff"]}><EmploymentVerificationPage /></ProtectedRoute>} />
            <Route path="/staff-ops/landlord/:sid" element={<ProtectedRoute roles={["ntn_staff"]}><LandlordVerificationPage /></ProtectedRoute>} />
            <Route path="/staff-ops/documents/:sid" element={<ProtectedRoute roles={["ntn_staff"]}><DocumentValidationPage /></ProtectedRoute>} />
            <Route path="/collections" element={<ProtectedRoute roles={["property_manager", "ntn_staff"]}><CollectionsPage /></ProtectedRoute>} />
            <Route path="/collections/new" element={<ProtectedRoute roles={["property_manager"]}><NewClaimPage /></ProtectedRoute>} />
            <Route path="/collections/:id" element={<ProtectedRoute roles={["property_manager", "ntn_staff"]}><ClaimDetailPage /></ProtectedRoute>} />
            <Route path="/groups/:id" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
            <Route path="/outreach" element={<ProtectedRoute roles={["property_manager", "ntn_staff"]} requiredPermission="reports.order"><OutreachPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
